import { useCallback } from 'react';

import { useButtonPressedCallback } from '@zenbusiness/application-commons-clickstream';
import isNotUndefinedOrNull from '@zenbusiness/application-commons-utils/isNotUndefinedOrNull';
import Box from '@zenbusiness/zui/Box';
import ErrorState from '@zenbusiness/zui/ErrorState';
import SharedAsset from '@zenbusiness/zui/SharedAsset';
import Stack from '@zenbusiness/zui/Stack';
import { styled } from '@zenbusiness/zui/styles';
import { useNavigate } from 'react-router-dom';

import AddCounterPartyModal from '#client/components/AddCounterPartyModal';
import { MODAL_TYPES } from '#client/components/AddCounterPartyModal/constants/types';
import AstraAuthorizationModal from '#client/components/AstraAuthorizationModal';
import ErrorBoundary from '#client/components/ErrorBoundary';
import InstanceDepositMoneyModal from '#client/components/MoveMoney/InstantDepositModal';
import { TRY_ADD_CARD_AGAIN_STEP } from '#client/components/MoveMoney/InstantDepositModal/constants/instants';
import { REFRESH_AFTER_INSTANT_DEPOSIT } from '#client/constants/eventName';
import { RELEASE_SHOW_INSTANT_DEPOSIT } from '#client/constants/experimentsFlags';
import { COUNTER_PARTY_STATUS, GET_COUNTER_PARTIES_CONNECT_TYPE } from '#client/constants/payees';
import useAstraProfileContext from '#client/hooks/useAstraProfileContext';
import useCheckOpenModal from '#client/hooks/useCheckOpenModals';
import useConstBusinessEntityUuid from '#client/hooks/useConstBusinessEntityUuid';
import useCounterparty from '#client/hooks/useCounterparty';
import useDisableDashboardContext from '#client/hooks/useDisableDashboardContext';
import useEventListeners from '#client/hooks/useEventListeners';
import useFeatureFlag from '#client/hooks/useFeatureFlag';
import useInstantBankingCard from '#client/hooks/useInstantBankingCard';
import useInstantDepositLimit from '#client/hooks/useInstantDepositLimit';
import useModalContext from '#client/hooks/useModalContext';
import useOpenAstraModal from '#client/hooks/useOpenAstraModal';
import useRedirectToLinkedAccounts, {
  LINK_INTENT
} from '#client/hooks/useRedirectToLinkedAccounts';
import useUnitAccountId from '#client/hooks/useUnitAccountId';

import SectionTitle from '../SectionTitle';
import Banks from './components/Banks';
import DebitCards from './components/DebitCardsOriginal';
import EmptyState from './components/EmptyState';
import Payees from './components/Payees';
import { ConnectedAccountsWrapperStyle } from './ConnectedAccount.styles';

const ConnectedAccountsWrapperStyled = styled(Stack)(ConnectedAccountsWrapperStyle);

export default function ConnectedAccountOriginal() {
  const navigate = useNavigate();
  const { businessEntityUuid } = useConstBusinessEntityUuid();
  const { redirectToLinkedAccounts } = useRedirectToLinkedAccounts(businessEntityUuid);
  const {
    hasAcceptedTerms,
    loading: loadingAstraProfile,
    isNeedCreateDestinationCard,
    refreshAstraProfile,
    cardList,
    isValidAccountStatus
  } = useAstraProfileContext();

  const {
    loading: instantDepositLimitLoading,
    transfers: instantDepositTransfers,
    refetchInstantDepositLimit
  } = useInstantDepositLimit();
  const { isDailyTransferLimitExceeded } = instantDepositTransfers;

  // we need to listen for this to fetch updated isDailyTransferLimitExceeded after an instant deposit transfer.
  useEventListeners([
    { eventName: REFRESH_AFTER_INSTANT_DEPOSIT, eventHandler: refetchInstantDepositLimit }
  ]);

  const { createBankingCardForInstantFunding, loading: createCardLoading } =
    useInstantBankingCard();
  const { openModal } = useModalContext();
  const { unitAccountId, isValidMfa, isEnabled, isEnrolled, isFetchingMfaStatus } =
    useUnitAccountId();
  const { isOpeningModal } = useCheckOpenModal();
  const { isDisable } = useDisableDashboardContext();
  const { getAndRedirectAstraUrl } = useOpenAstraModal();

  const [isShowInstantDepositFlagEnabled, isShowInstantDepositFlagLoading] = useFeatureFlag(
    RELEASE_SHOW_INSTANT_DEPOSIT
  );

  const shouldHideInstantDepositOption =
    !isShowInstantDepositFlagLoading && !isShowInstantDepositFlagEnabled;

  const isSkipQuery =
    isFetchingMfaStatus ||
    !isEnrolled ||
    !isEnabled ||
    !isValidMfa ||
    !unitAccountId ||
    isOpeningModal;

  const {
    data: payeesData,
    error: payeesError,
    accounts: payeesAccounts,
    loadingAccounts: loadingPayeesAccounts,
    refetch: refetchPayeesAccounts
  } = useCounterparty(
    GET_COUNTER_PARTIES_CONNECT_TYPE.PAYEE,
    COUNTER_PARTY_STATUS.ACTIVE,
    isSkipQuery,
    3
  );

  const {
    data: banksData,
    error: banksError,
    accounts: banksAccounts,
    loadingAccounts: loadingBanksAccounts,
    refetch: refetchBanksAccounts
  } = useCounterparty(
    GET_COUNTER_PARTIES_CONNECT_TYPE.PLAID,
    COUNTER_PARTY_STATUS.ALL,
    isSkipQuery,
    3
  );

  const showDebitCardSection = !shouldHideInstantDepositOption && isValidAccountStatus;

  // Check if payeesAccounts is empty based on loading and data conditions
  const payeesAccountsEmpty =
    // Case 1: Payees data is defined, not loading, and payeesAccounts is empty
    (isNotUndefinedOrNull(loadingPayeesAccounts) &&
      isNotUndefinedOrNull(payeesData) &&
      !loadingPayeesAccounts &&
      payeesAccounts.length === 0) ||
    // Case 2: Payees data is undefined and not loading
    (!loadingPayeesAccounts && payeesData === undefined);

  // Check if banksAccounts is empty based on loading and data conditions
  const banksAccountsEmpty =
    // Case 1: Banks data is defined, not loading, and banksAccounts is empty
    (isNotUndefinedOrNull(loadingBanksAccounts) &&
      isNotUndefinedOrNull(banksData) &&
      !loadingBanksAccounts &&
      banksAccounts.length === 0) ||
    // Case 2: Banks data is undefined and not loading
    (!loadingBanksAccounts && banksData === undefined);

  // Check if astra debit cards is empty based on loading, data conditions and feature flag
  // if the feature flag is disabled, set debitCardsEmpty to true
  const debitCardsEmpty =
    shouldHideInstantDepositOption ||
    (isNotUndefinedOrNull(loadingAstraProfile) &&
      isNotUndefinedOrNull(cardList) &&
      !loadingAstraProfile &&
      cardList.length === 0) ||
    (!loadingAstraProfile && cardList === undefined);

  /**
   * Handles refetching data after adding a new item.
   *
   * @param {string} mode - The mode for adding (bank or payee).
   */
  const refetchAfterAddNewItemHandler = (mode) => {
    // Refetch bank accounts.
    const refetchBanks = () => refetchBanksAccounts();

    // Refetch both bank and payee accounts.
    const refetchBanksAndPayees = () => {
      refetchBanksAccounts();
      refetchPayeesAccounts();
    };

    // Determine the mode and call the appropriate refetch function(s)
    // eslint-disable-next-line no-unused-expressions
    mode === MODAL_TYPES.ADD_BANK ? refetchBanks() : refetchBanksAndPayees();
  };

  /**
   * Combine the conditions to check if payees, banks accounts and Astra debit cards lists are empty
   * - If `emptyConnectedAccounts` is true, it means payees, banks accounts and debit cards lists are empty.
   * - If `emptyConnectedAccounts` is false, at least one of the lists has items or is still loading.
   */
  const emptyConnectedAccounts = payeesAccountsEmpty && banksAccountsEmpty && debitCardsEmpty;

  /**
   * Handles the addition of a bank or payee.
   * Note: this UI showing the dashboard,
   * so we don't need to pass the `entryPageForSendMoney`
   * it will get default value instead of
   *
   * @param {string} mode - The mode indicating whether it's for adding a bank or payee.
   * @returns {void}
   */
  const addBankOrPayeeHandler = (mode) => {
    if (isDisable && banksError && payeesError) {
      return;
    }

    const isBankMode = mode === MODAL_TYPES.ADD_BANK || mode === MODAL_TYPES.ADD_BANK_AND_PAYEE;
    const openAddCounterPartyModal = () =>
      openModal({
        mode,
        component: AddCounterPartyModal,
        refetch: () => refetchAfterAddNewItemHandler(mode)
      });

    if (mode === MODAL_TYPES.ADD_PAYEE) {
      openAddCounterPartyModal();
      return;
    }

    if (isBankMode && redirectToLinkedAccounts({ navigate, intent: LINK_INTENT })) {
      return;
    }

    openAddCounterPartyModal();
  };

  const buttonPressedTracker = useButtonPressedCallback({
    elementName: 'add_a_card_on_dashboard&c',
    elementText: 'Add a card',
    appPageName: 'active_banking_dashboard'
  });

  const addDebitCardHandler = useCallback(async () => {
    /**
     * Tracking Details: Tracking added to Add a card link on dashboard
     * Question: How many customers click on Add a card link?
     */
    buttonPressedTracker();
    if (!hasAcceptedTerms) {
      openModal({
        component: AstraAuthorizationModal
      });
      return;
    }

    if (isNeedCreateDestinationCard) {
      const { error: createCardError } = await createBankingCardForInstantFunding();
      if (createCardError) {
        openModal({
          component: InstanceDepositMoneyModal,
          initStep: TRY_ADD_CARD_AGAIN_STEP
        });

        return;
      }

      // After create destination card successfully, refresh the astra profile
      refreshAstraProfile();
    }

    if (hasAcceptedTerms) {
      getAndRedirectAstraUrl();
    }
  }, [
    buttonPressedTracker,
    hasAcceptedTerms,
    isNeedCreateDestinationCard,
    openModal,
    createBankingCardForInstantFunding,
    refreshAstraProfile,
    getAndRedirectAstraUrl
  ]);

  return (
    <Stack
      gap={{ xs: '8px', md: '12px' }}
      marginBottom={{ xs: '8px', md: '12px' }}
      marginTop={{ xs: '16px', md: '32px' }}
    >
      <>
        <SectionTitle>Connected Accounts</SectionTitle>
        <ConnectedAccountsWrapperStyled spacing={{ xs: '16px', md: '32px' }}>
          <ErrorBoundary
            fallback={
              <ErrorState
                slotProps={{
                  primaryAction: {
                    eventProps: {
                      pageSection: 'banking'
                    }
                  }
                }}
              />
            }
          >
            {emptyConnectedAccounts ? (
              <Stack
                alignItems="center"
                mt="28px !important"
              >
                <Box sx={{ width: '66px', height: '49px' }}>
                  <SharedAsset
                    alt=""
                    height={49}
                    src="/s2/vector/banking-linked-accounts-disabled.svg"
                    width={66}
                  />
                </Box>
                <Box
                  p="16px 0px"
                  textAlign="center"
                >
                  <EmptyState
                    onClick={() => addBankOrPayeeHandler(MODAL_TYPES.ADD_BANK_AND_PAYEE)}
                    text="Add a bank or payee"
                  />
                </Box>
              </Stack>
            ) : (
              <>
                <Banks
                  accounts={banksAccounts}
                  addBankHandler={() => addBankOrPayeeHandler(MODAL_TYPES.ADD_BANK)}
                  loading={loadingBanksAccounts}
                />
                <Payees
                  accounts={payeesAccounts}
                  addPayeeHandler={() => addBankOrPayeeHandler(MODAL_TYPES.ADD_PAYEE)}
                  data={payeesData}
                  loading={loadingPayeesAccounts}
                  refetch={refetchPayeesAccounts}
                />
                {showDebitCardSection ? (
                  <DebitCards
                    accounts={cardList}
                    addDebitCardHandler={addDebitCardHandler}
                    createBankingCardForInstantFunding={createBankingCardForInstantFunding}
                    isDailyTransferLimitExceeded={isDailyTransferLimitExceeded}
                    loading={loadingAstraProfile || createCardLoading || instantDepositLimitLoading}
                  />
                ) : null}
              </>
            )}
          </ErrorBoundary>
        </ConnectedAccountsWrapperStyled>
      </>
    </Stack>
  );
}
